import React, { useEffect, useState, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
type Language = 'english' | 'pidgin' | 'edo';
type UserType = 'user' | 'admin' | 'super_admin' | 'guest';
interface EsosaContextType {
  isOpen: boolean;
  toggleAssistant: () => void;
  closeAssistant: () => void;
  openAssistant: () => void;
  messages: Message[];
  addMessage: (content: string, sender: 'user' | 'assistant') => void;
  sendMessage: (content: string) => void;
  currentLanguage: Language;
  setLanguage: (language: Language) => void;
  isLoading: boolean;
  userType: UserType;
  currentPage: string;
  clearMessages: () => void;
}
export interface Message {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}
const EsosaContext = createContext<EsosaContextType | undefined>(undefined);
export const useEsosa = () => {
  const context = useContext(EsosaContext);
  if (context === undefined) {
    throw new Error('useEsosa must be used within an EsosaProvider');
  }
  return context;
};
interface EsosaProviderProps {
  children: ReactNode;
}
export const EsosaProvider: React.FC<EsosaProviderProps> = ({
  children
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentLanguage, setCurrentLanguage] = useState<Language>('english');
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const {
    user
  } = useAuth();
  const {
    admin
  } = useAdminAuth();
  // Determine user type
  const userType: UserType = admin ? admin.role === 'super_admin' ? 'super_admin' : 'admin' : user ? 'user' : 'guest';
  // Get current page from location
  const currentPage = location.pathname;
  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMessages = {
        english: "Hello! I'm Esosa, your AI assistant for the Edo State E-Services Portal. How can I help you today?",
        pidgin: 'How far! I be Esosa, your AI assistant for Edo State E-Services Portal. Wetin I fit do for you today?',
        edo: 'Koyo! Me noye Esosa, AI assistant for Edo State E-Services Portal. Vbo me ya ru we ghee?'
      };
      addMessage(welcomeMessages[currentLanguage], 'assistant');
    }
  }, [currentLanguage]);
  // Update welcome message when language changes
  useEffect(() => {
    if (messages.length > 0) {
      const welcomeMessages = {
        english: "I've switched to English. How can I help you?",
        pidgin: 'I don change to Pidgin. Wetin I fit do for you?',
        edo: 'Me ghi rhie Edo language. Vbo me ya ru we ghee?'
      };
      addMessage(welcomeMessages[currentLanguage], 'assistant');
    }
  }, [currentLanguage]);
  // Context-aware suggestions based on current page
  useEffect(() => {
    if (isOpen && messages.length === 1) {
      const pageContextMessages = {
        '/dashboard': {
          english: "I notice you're on the dashboard. Would you like help with navigating services, checking application status, or understanding recent notifications?",
          pidgin: 'I see say you dey dashboard. You need help to find services, check your application status, or understand your notifications?',
          edo: 'Me ri re ne dashboard. Wa gha mwen irri services, check application status, kpee mwen aro notifications?'
        },
        '/mdas': {
          english: 'Looking for a specific government service? I can help you find the right Ministry, Department or Agency for your needs.',
          pidgin: 'You dey find particular government service? I fit help you see the correct Ministry, Department or Agency wey you need.',
          edo: 'Wa gha guen specific government service? Me ya ru we rue Ministry, Department kpee Agency ne wa gha rhie.'
        },
        '/profile': {
          english: 'Need help updating your profile information? I can guide you through the process.',
          pidgin: 'You want update your profile? I fit show you how.',
          edo: 'Wa gha update we profile? Me ya ru we rue.'
        }
        // Add more page-specific contexts as needed
      };
      const contextPath = Object.keys(pageContextMessages).find(path => currentPage.startsWith(path));
      if (contextPath && pageContextMessages[contextPath as keyof typeof pageContextMessages]) {
        const contextMessage = pageContextMessages[contextPath as keyof typeof pageContextMessages][currentLanguage];
        setTimeout(() => {
          addMessage(contextMessage, 'assistant');
        }, 1000);
      }
    }
  }, [currentPage, isOpen]);
  const toggleAssistant = () => setIsOpen(!isOpen);
  const closeAssistant = () => setIsOpen(false);
  const openAssistant = () => setIsOpen(true);
  const addMessage = (content: string, sender: 'user' | 'assistant') => {
    const newMessage: Message = {
      id: Date.now().toString(),
      content,
      sender,
      timestamp: new Date()
    };
    setMessages(prevMessages => [...prevMessages, newMessage]);
  };
  const clearMessages = () => {
    setMessages([]);
    // Re-add welcome message
    const welcomeMessages = {
      english: "Hello! I'm Esosa, your AI assistant for the Edo State E-Services Portal. How can I help you today?",
      pidgin: 'How far! I be Esosa, your AI assistant for Edo State E-Services Portal. Wetin I fit do for you today?',
      edo: 'Koyo! Me noye Esosa, AI assistant for Edo State E-Services Portal. Vbo me ya ru we ghee?'
    };
    addMessage(welcomeMessages[currentLanguage], 'assistant');
  };
  const sendMessage = async (content: string) => {
    if (!content.trim()) return;
    // Add user message
    addMessage(content, 'user');
    // Simulate AI processing
    setIsLoading(true);
    try {
      // In a real implementation, this would be an API call to your AI service
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Generate response based on user type, current page, and message content
      let response = '';
      // Simple response logic based on keywords
      const lowerContent = content.toLowerCase();
      if (lowerContent.includes('hello') || lowerContent.includes('hi') || lowerContent.includes('hey')) {
        if (currentLanguage === 'english') {
          response = 'Hello! How can I assist you with the Edo State E-Services Portal today?';
        } else if (currentLanguage === 'pidgin') {
          response = 'How far! Wetin I fit help you with for Edo State E-Services Portal today?';
        } else {
          response = 'Koyo! Vbo me ya ru we for Edo State E-Services Portal ghee?';
        }
      } else if (lowerContent.includes('service') || lowerContent.includes('apply')) {
        if (currentLanguage === 'english') {
          response = 'To apply for a service, navigate to the Services section from your dashboard or use the MDAs menu to find the specific service you need. Would you like me to guide you through the process?';
        } else if (currentLanguage === 'pidgin') {
          response = 'To apply for service, go to Services section for your dashboard or use MDAs menu find the service wey you need. You want make I show you how?';
        } else {
          response = 'Ne wa gha apply for service, wa gu Services section ne dashboard kpee MDAs menu rue service ne wa gha rhie. Wa ghi mwen ru me ya ru we rue?';
        }
      } else if (lowerContent.includes('payment') || lowerContent.includes('pay')) {
        if (currentLanguage === 'english') {
          response = "You can make payments for services through the portal using debit cards, bank transfers, or USSD. Your payment history is available in the 'Payment History' section. Would you like to know more about payment options?";
        } else if (currentLanguage === 'pidgin') {
          response = "You fit pay for services through portal with debit card, bank transfer, or USSD. Your payment history dey for 'Payment History' section. You want know more about how to pay?";
        } else {
          response = "Wa ya pay for services through portal with debit card, bank transfer, kpee USSD. We payment history no 'Payment History' section. Wa ghi mwen ru more about payment options?";
        }
      } else if (lowerContent.includes('help') || lowerContent.includes('support')) {
        if (currentLanguage === 'english') {
          response = "I'm here to help! You can also contact our support team through the 'Help & Support' section, call our helpline at +234 800 EDO HELP, or email support@edostate.gov.ng. What do you need assistance with?";
        } else if (currentLanguage === 'pidgin') {
          response = "I dey here to help you! You fit also contact our support team through 'Help & Support' section, call our helpline for +234 800 EDO HELP, or email support@edostate.gov.ng. Wetin you need help with?";
        } else {
          response = "Me ye kpaotoe we! Wa ya contact support team through 'Help & Support' section, call helpline for +234 800 EDO HELP, kpee email support@edostate.gov.ng. Vbo wa gha rhie assistance with?";
        }
      } else if (lowerContent.includes('document') || lowerContent.includes('upload')) {
        if (currentLanguage === 'english') {
          response = "To upload documents, go to the specific service application form. You'll see document upload fields where you can attach your files. Make sure your documents are in PDF, JPG, or PNG format and under 5MB each.";
        } else if (currentLanguage === 'pidgin') {
          response = 'To upload documents, go to the service application form. You go see where you fit upload your files. Make sure say your documents na PDF, JPG, or PNG and no big pass 5MB each.';
        } else {
          response = 'Ne wa gha upload documents, wa gu service application form. Wa ghi ri fields ne wa ya upload we files. Make sure say we documents ye PDF, JPG, kpee PNG format kpee e no big pass 5MB each.';
        }
      } else {
        // Default responses
        if (currentLanguage === 'english') {
          response = "I understand you're asking about " + content + '. How can I provide more specific help with this? You can ask about services, applications, payments, or technical support.';
        } else if (currentLanguage === 'pidgin') {
          response = 'I understand say you dey ask about ' + content + '. How I fit give you better help for this matter? You fit ask about services, applications, payments, or technical support.';
        } else {
          response = 'Me mwen ru wa gha ask about ' + content + '. Vbo me ya ru we more specific help with this? Wa ya ask about services, applications, payments, kpee technical support.';
        }
      }
      // Add assistant response
      addMessage(response, 'assistant');
    } catch (error) {
      console.error('Error generating response:', error);
      // Fallback error message
      const errorMessages = {
        english: "I'm sorry, I encountered an error processing your request. Please try again later.",
        pidgin: 'Sorry, wahala happen as I dey process your request. Abeg try again later.',
        edo: 'Epa, error occur as me gha process we request. Biko, try again later.'
      };
      addMessage(errorMessages[currentLanguage], 'assistant');
    } finally {
      setIsLoading(false);
    }
  };
  const setLanguage = (language: Language) => {
    setCurrentLanguage(language);
  };
  return <EsosaContext.Provider value={{
    isOpen,
    toggleAssistant,
    closeAssistant,
    openAssistant,
    messages,
    addMessage,
    sendMessage,
    currentLanguage,
    setLanguage,
    isLoading,
    userType,
    currentPage,
    clearMessages
  }} data-id="element-268">
      {children}
    </EsosaContext.Provider>;
};